import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AnalyseIADossier.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Markdown léger, même grammaire que GoogleAdsChat (titres ##, listes, gras).
function formatInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part);
}
function formatMessage(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) return <h5 key={i} className="aid-md-h">{formatInline(line.slice(4))}</h5>;
    if (line.startsWith('## ')) return <h4 key={i} className="aid-md-h">{formatInline(line.slice(3))}</h4>;
    if (line.startsWith('# ')) return <h4 key={i} className="aid-md-h">{formatInline(line.slice(2))}</h4>;
    if (line.match(/^\s*[-•*]\s/)) return <li key={i} className="aid-md-li">{formatInline(line.replace(/^\s*[-•*]\s/, ''))}</li>;
    if (line.match(/^\s*\d+\.\s/)) return <li key={i} className="aid-md-li">{formatInline(line.replace(/^\s*\d+\.\s/, ''))}</li>;
    if (!line.trim()) return null;
    return <p key={i} className="aid-md-p">{formatInline(line)}</p>;
  });
}

const fmtUsd = (v) => (v == null ? '' : `${Number(v).toFixed(v < 0.01 ? 4 : 3)} $`);
const fmtQuand = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '' : x.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const SUGGESTIONS = [
  'Quel contrôle faire en premier pour trancher entre tes hypothèses ?',
  'Le compagnon confirme une fuite au niveau du carter : que faut-il vérifier ?',
  'Rédige les questions à poser au client au téléphone.',
  'Résume le dossier en 5 lignes pour le chef d\'atelier.',
];

// Onglet « Analyse IA » de la modale d'intervention — chat persistant par
// dossier, super-admin uniquement. Le contexte du dossier n'est JAMAIS envoyé
// par le navigateur : le serveur le reconstruit et l'anonymise à chaque tour.
export default function AnalyseIADossier({ interventionId, numeroOr }) {
  const [etat, setEtat] = useState('chargement'); // chargement | pret | indisponible
  const [infos, setInfos] = useState(null);       // { modele, max_photos, cout, medias }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [erreur, setErreur] = useState(null);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  const charger = useCallback(async () => {
    if (!interventionId) return;
    try {
      const r = await api.get(`/planning/interventions/${interventionId}/analyse-ia`);
      setInfos({ modele: r.data.modele, max_photos: r.data.max_photos, cout: r.data.cout, medias: r.data.medias });
      setMessages(r.data.messages || []);
      setEtat('pret');
    } catch (e) {
      setEtat('indisponible');
      setErreur(e?.response?.data?.error || 'Analyse IA indisponible.');
    }
  }, [interventionId]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => () => { abortRef.current?.abort(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages, streamContent]);

  const envoyer = async (texte) => {
    const content = (texte ?? input).trim();
    const premiere = messages.length === 0;
    if (streaming || (!premiere && !content)) return;
    setErreur(null); setInput(''); setStreaming(true); setStreamContent('');
    if (!premiere) setMessages(p => [...p, { role: 'user', kind: 'message', content, created_at: new Date().toISOString(), auteur: null, _local: true }]);
    else setMessages([{ role: 'user', kind: 'analyse', content: 'Analyse lancée', created_at: new Date().toISOString(), _local: true }]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let acc = '';
    let fini = false;
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/planning/interventions/${interventionId}/analyse-ia/stream`, {
        method: 'POST', signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: premiere ? '' : content }),
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try { msg = (await resp.json()).error || msg; } catch { /* corps non JSON */ }
        throw new Error(msg);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const s = line.slice(6).trim();
          if (!s) continue;
          let ev; try { ev = JSON.parse(s); } catch { continue; }
          if (ev.type === 'token') { acc += ev.content; setStreamContent(acc); }
          else if (ev.type === 'full') { acc = ev.content; setStreamContent(acc); }
          else if (ev.type === 'user_saved') {
            setMessages(p => p.map(m => (m._local ? { ...m, _local: false, id: ev.id, created_at: ev.created_at, photos_ids: new Array(ev.photos || 0).fill(0) } : m)));
          }
          else if (ev.type === 'done') {
            fini = true;
            const c = acc;
            setMessages(p => [...p, { id: ev.id, role: 'assistant', kind: 'message', content: c, created_at: ev.created_at, cout_usd: ev.cout_usd, tokens_in: ev.tokens_in, tokens_out: ev.tokens_out, model: ev.model }]);
            setStreamContent('');
            setInfos(i => (i ? { ...i, cout: { total_usd: (i.cout?.total_usd || 0) + (ev.cout_usd || 0), reponses: (i.cout?.reponses || 0) + 1 } } : i));
          }
          else if (ev.type === 'error') { fini = true; setErreur(ev.content || 'Erreur.'); setStreamContent(''); }
        }
      }
      if (!fini) {
        // Flux coupé sans `done` : on garde ce qui est arrivé, le serveur l'a
        // peut-être enregistré — un rechargement remettra le fil d'aplomb.
        if (acc) setMessages(p => [...p, { role: 'assistant', kind: 'message', content: acc, created_at: new Date().toISOString() }]);
        setStreamContent('');
        charger();
      }
    } catch (e) {
      if (e.name !== 'AbortError') setErreur(e.message || 'Erreur de connexion.');
      setStreamContent('');
      charger();
    } finally { setStreaming(false); abortRef.current = null; }
  };

  const nouvelleAnalyse = async () => {
    if (streaming) return;
    if (!window.confirm('Archiver ce fil et repartir de zéro ? L\'ancien fil ne sera plus affiché.')) return;
    try {
      await api.post(`/planning/interventions/${interventionId}/analyse-ia/reset`);
      setMessages([]); setErreur(null);
      setInfos(i => (i ? { ...i, cout: { total_usd: 0, reponses: 0 } } : i));
    } catch (e) {
      setErreur(e?.response?.data?.error || 'Impossible de réinitialiser.');
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(); } };

  if (etat === 'chargement') return <div className="aid aid-vide"><p className="aid-muted">Chargement…</p></div>;
  if (etat === 'indisponible') return <div className="aid aid-vide"><p className="aid-erreur">{erreur}</p></div>;

  const medias = infos?.medias || { photos: 0, videos: 0 };
  const vide = messages.length === 0 && !streaming;

  return (
    <div className="aid">
      <div className="aid-entete">
        <div>
          <div className="aid-titre">🤖 Analyse IA du dossier <span className="aid-sa">🔒 super-admin</span></div>
          <div className="aid-sous">
            {numeroOr ? `OR ${numeroOr} · ` : 'Sans n° d\'OR · '}
            {medias.photos} photo{medias.photos > 1 ? 's' : ''}
            {medias.photos > (infos?.max_photos || 12) ? ` (${infos.max_photos} jointes)` : ''}
            {medias.videos ? ` · ${medias.videos} vidéo${medias.videos > 1 ? 's' : ''} (non analysée${medias.videos > 1 ? 's' : ''})` : ''}
            {' · '}{infos?.modele}
            {infos?.cout?.reponses ? ` · ${infos.cout.reponses} réponse${infos.cout.reponses > 1 ? 's' : ''} · ${fmtUsd(infos.cout.total_usd)}` : ''}
          </div>
        </div>
        {!vide && (
          <button type="button" className="aid-btn-secondaire" onClick={nouvelleAnalyse} disabled={streaming}>
            ↺ Nouvelle analyse
          </button>
        )}
      </div>

      <div className="aid-fil">
        {vide && (
          <div className="aid-intro">
            <p>
              L'IA lit tout le dossier — plainte, travaux, kilométrage, compte rendu, fil du dossier,
              historique du véhicule dans le groupe (factures, garantie, rappels, pièces) et les photos de
              l'atelier — puis propose des hypothèses de diagnostic et des points à vérifier. Tu peux ensuite
              préciser, corriger, et itérer.
            </p>
            <p className="aid-muted">
              Identité, téléphone, e-mail, immatriculation et VIN complet ne sont jamais transmis ; les noms
              des collaborateurs sont remplacés par des jetons. Les photos partent telles quelles.
            </p>
            <button type="button" className="aid-btn-principal" onClick={() => envoyer('')} disabled={streaming}>
              ▶ Lancer l'analyse
            </button>
          </div>
        )}

        {messages.map((m, i) => (
          m.kind === 'analyse'
            ? (
              <div key={m.id || i} className="aid-jalon">
                Analyse lancée{m.auteur ? ` par ${m.auteur}` : ''}{m.created_at ? ` · ${fmtQuand(m.created_at)}` : ''}
                {m.photos_ids?.length ? ` · ${m.photos_ids.length} photo${m.photos_ids.length > 1 ? 's' : ''} jointe${m.photos_ids.length > 1 ? 's' : ''}` : ''}
              </div>
            ) : (
              <div key={m.id || i} className={`aid-msg aid-msg-${m.role}`}>
                <div className="aid-msg-content">
                  {m.role === 'assistant' ? formatMessage(m.content) : m.content}
                </div>
                <div className="aid-msg-meta">
                  {m.role === 'user' && m.auteur ? `${m.auteur} · ` : ''}
                  {fmtQuand(m.created_at)}
                  {m.role === 'user' && m.photos_ids?.length ? ` · +${m.photos_ids.length} photo${m.photos_ids.length > 1 ? 's' : ''}` : ''}
                  {m.role === 'assistant' && m.cout_usd != null ? ` · ${fmtUsd(m.cout_usd)}` : ''}
                </div>
              </div>
            )
        ))}

        {streaming && (
          <div className="aid-msg aid-msg-assistant">
            <div className="aid-msg-content">
              {streamContent ? formatMessage(streamContent) : <span className="aid-muted">Lecture du dossier et des photos…</span>}
              <span className="aid-cursor" />
            </div>
          </div>
        )}
        {erreur && <div className="aid-erreur">⚠️ {erreur}</div>}
        <div ref={endRef} />
      </div>

      {!vide && (
        <>
          {messages.length > 0 && messages.length < 3 && !streaming && (
            <div className="aid-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="aid-chip" onClick={() => envoyer(s)}>{s}</button>
              ))}
            </div>
          )}
          <div className="aid-saisie">
            <textarea
              rows={2}
              placeholder="Apporte une précision, un résultat de contrôle, une question… (Entrée pour envoyer)"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              disabled={streaming}
            />
            {streaming
              ? <button type="button" className="aid-btn-secondaire" onClick={() => abortRef.current?.abort()}>■ Stop</button>
              : <button type="button" className="aid-btn-principal" onClick={() => envoyer()} disabled={!input.trim()}>➤</button>}
          </div>
        </>
      )}
      <p className="aid-disclaimer">
        L'IA peut se tromper : ses hypothèses ne remplacent ni le diagnostic du compagnon ni la validation du chef d'atelier.
        Données anonymisées et photos transmises à Anthropic (États-Unis).
      </p>
    </div>
  );
}
